'use client';
// Dashboard — the live pulse of the selected network
import {
  Box, Flame, Gauge, Layers, Pickaxe, ShieldCheck, TrendingDown,
  Wallet, Coins, FileText, Activity, Cpu, KeyRound, Lock, Gem, Atom, Network,
} from 'lucide-react';
import { fetchJson, usePoll, fmtAmt, fmtInt, fmtHashrate, timeAgo, StatCard, SectionHeader, Mono, TypeBadge, shortHash, Spinner, QuantumBadge } from './shared';
import type { Net, NetStats, MiningStatus, BlockT, Tx } from './types';
import { Progress } from '@/components/ui/progress';

export function Dashboard({ net, onNavigate }: { net: Net; onNavigate: (tab: string) => void }) {
  const { data } = usePoll<{ launched: boolean; network: NetStats; mining: MiningStatus }>(
    async () => fetchJson(`/api/stats?net=${net}`), 2500, [net]);
  const { data: blocksData } = usePoll<{ blocks: BlockT[] }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=blocks&limit=6`), 3000, [net]);
  const { data: txsData } = usePoll<{ txs: Tx[] }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=txs&limit=8`), 3000, [net]);

  const n = data?.network;
  const m = data?.mining;
  const supplyPct = n ? (n.supply / n.maxSupply) * 100 : 0;

  const features = [
    { icon: Atom, title: 'Hybrid quantum shield', desc: 'Every transaction is signed twice: Ed25519 + ML-DSA-87 (NIST FIPS 204, Category 5). Consensus structurally rejects anything without the quantum signature.', quantum: true },
    { icon: KeyRound, title: 'Deterministic Ed25519', desc: 'The classic layer uses deterministic signatures resistant to side-channel leakage — no reliance on per-signature randomness.', quantum: false },
    { icon: Lock, title: 'AES-256-GCM vaults', desc: 'The master seed is encrypted at rest with an scrypt-derived key — stealing the wallet file is not enough to steal the funds.', quantum: false },
    { icon: Flame, title: 'Deflationary fee burn', desc: '50% of every transaction fee is permanently burned, and 100% of token deployment fees — supply shrinks as usage grows.', quantum: false },
    { icon: Layers, title: 'Chain-ID replay protection', desc: 'The network identifier is embedded inside both signatures — transactions cannot be replayed on the other network.', quantum: false },
    { icon: Gem, title: 'AMT-20 native tokens', desc: 'Deploy tokens directly in consensus — supply caps, minting and burning enforced by the protocol, not by external contracts.', quantum: false },
  ];

  return (
    <div className="space-y-6">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border glass">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative p-6 md:p-10">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 px-3 py-1 text-xs font-bold text-emerald-400">
              <span className="pulse-dot" /> {n?.networkName ?? 'Network'} live — block #{fmtInt(n?.height ?? 0)}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 px-3 py-1 text-xs font-bold text-amber-400">
              <Cpu className="size-3.5" /> {m?.running ? `Mining at ${fmtHashrate(m.hashRate)}` : `Last block ${fmtHashrate(n?.lastBlock?.hashRate ?? 0)}`}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/10 ring-1 ring-sky-500/30 px-3 py-1 text-xs font-bold text-sky-400">
              <Network className="size-3.5" /> Chain-ID {n?.chainId ?? '—'}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight">
            <span className="grad-text">AmanChain</span>
            <span className="text-muted-foreground text-lg md:text-2xl font-bold ml-3">AMAN</span>
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mt-3 leading-relaxed">
            A ledger engineered for the quantum era: every transaction carries a mandatory
            <b className="text-cyan-300"> ML-DSA-87 </b> post-quantum signature (NIST FIPS 204) on top of
            <b className="text-foreground"> Ed25519</b> — verified by consensus, enforced on every block.
            A capped 100M supply, deflationary fee burns and the AMT-20 token standard complete the platform.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => onNavigate('quantum')} className="rounded-lg bg-cyan-500 text-[#04060c] font-black text-sm px-5 py-2.5 hover:opacity-90 transition shadow-lg shadow-cyan-500/25 inline-flex items-center gap-2">
              <Atom className="size-4" /> Verify the shield live
            </button>
            <button onClick={() => onNavigate('wallets')} className="rounded-lg bg-primary text-primary-foreground font-bold text-sm px-5 py-2.5 hover:opacity-90 transition shadow-lg shadow-emerald-500/20">
              Create a wallet
            </button>
            <button onClick={() => onNavigate('tokens')} className="rounded-lg bg-secondary ring-1 ring-border font-bold text-sm px-5 py-2.5 hover:ring-primary/40 transition inline-flex items-center gap-2">
              <Gem className="size-4" /> Deploy a token
            </button>
            <button onClick={() => onNavigate('whitepaper')} className="rounded-lg bg-secondary ring-1 ring-border font-bold text-sm px-5 py-2.5 hover:ring-primary/40 transition inline-flex items-center gap-2">
              <FileText className="size-4" /> Whitepaper
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Box} label="Chain height" value={fmtInt(n?.height)} sub={`Block time ~${n ? n.avgBlockSec.toFixed(1) : '—'}s`} />
        <StatCard icon={Coins} label="Circulating supply" value={fmtAmt(n?.supply, 2)} sub={`of ${fmtInt(n?.maxSupply)} hard cap`} accent="gold" />
        <StatCard icon={Flame} label="Coins burned" value={fmtAmt(n?.totalBurned, 4)} sub="permanently destroyed" accent="rose" />
        <StatCard icon={Activity} label="Total transactions" value={fmtInt(n?.totalTxs)} sub={`${n?.mempoolSize ?? 0} pending`} accent="sky" />
        <StatCard icon={Gauge} label="Current difficulty" value={fmtInt(n?.difficulty)} sub={`target ${n?.targetBlockTime ?? 12}s per block`} />
        <StatCard icon={Pickaxe} label="Block reward" value={`${fmtAmt(n?.blockReward)} AMAN`} sub={`halving in ${fmtInt(n?.nextHalving)} blocks`} accent="gold" />
        <StatCard icon={Wallet} label="Wallets" value={fmtInt(n?.wallets)} sub={`${n?.accounts ?? 0} known addresses`} />
        <StatCard icon={Gem} label="AMT-20 tokens" value={fmtInt(n?.tokens)} sub="deployed on this network" accent="sky" />
      </div>

      {/* Quantum shield strip — evidence-first framing */}
      <section className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-gradient-to-r from-cyan-500/10 via-transparent to-transparent p-5 md:p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          <div className="rounded-2xl bg-cyan-500/10 ring-1 ring-cyan-400/40 p-3.5 text-cyan-300 shrink-0">
            <Atom className="size-8" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-black text-lg">Security built for the quantum era — provable, not promised</h2>
              <QuantumBadge />
            </div>
            <p className="text-xs md:text-sm text-muted-foreground mt-1.5 leading-relaxed">
              Signature schemes based on elliptic curves are exposed to Shor&apos;s algorithm on a sufficiently
              large quantum computer. AmanChain signs every transaction with a lattice-based scheme selected by
              NIST for exactly this future — and lets you verify it yourself: tamper a signature in the
              Quantum Lab and watch the node reject it.
            </p>
          </div>
          <button onClick={() => onNavigate('quantum')} className="rounded-lg ring-1 ring-cyan-400/40 bg-cyan-500/10 text-cyan-300 font-bold text-xs px-4 py-2.5 hover:bg-cyan-500/20 transition whitespace-nowrap">
            See the live proof →
          </button>
        </div>
      </section>

      {/* Supply curve */}
      <div className="glass p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm font-bold"><TrendingDown className="size-4 text-emerald-400" /> Supply toward the hard cap</div>
          <span className="text-xs text-muted-foreground" dir="ltr">{supplyPct.toFixed(4)}%</span>
        </div>
        <Progress value={supplyPct} className="h-2.5" />
        <p className="text-xs text-muted-foreground mt-2">
          Supply grows only through mining rewards that halve every 50,000 blocks and shrinks with every burned fee — a deflationary economy by design.
        </p>
      </div>

      {/* Blocks & transactions */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass p-5">
          <SectionHeader icon={Box} title="Latest blocks" action={<button onClick={() => onNavigate('explorer')} className="text-xs text-primary hover:underline">Explorer →</button>} />
          <div className="space-y-2 max-h-96 overflow-y-auto pl-1">
            {(blocksData?.blocks ?? []).map(b => (
              <button key={b.hash} onClick={() => onNavigate('explorer')} className="w-full text-left rounded-lg border border-border/60 bg-muted/30 p-3 hover:border-primary/40 transition">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-emerald-400" dir="ltr">#{fmtInt(b.height)}</span>
                  <span className="text-[0.68rem] text-muted-foreground">{timeAgo(b.timestamp)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <Mono>{shortHash(b.hash)}</Mono>
                  <span className="text-[0.68rem] text-amber-400 font-semibold">{fmtAmt(b.reward)} AMAN</span>
                </div>
              </button>
            ))}
            {!blocksData && <Spinner />}
          </div>
        </div>
        <div className="glass p-5">
          <SectionHeader icon={Activity} title="Latest transactions" action={<button onClick={() => onNavigate('explorer')} className="text-xs text-primary hover:underline">View all →</button>} />
          <div className="space-y-2 max-h-96 overflow-y-auto pl-1">
            {(txsData?.txs ?? []).map(t => (
              <div key={t.id} className="rounded-lg border border-border/60 bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-2">
                  <TypeBadge type={t.type} label={t.label} />
                  <span className="text-[0.68rem] text-muted-foreground">{t.pending ? 'pending…' : timeAgo(t.timestamp ?? Date.now())}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <Mono>{shortHash(t.id)}</Mono>
                  <span className="text-xs font-bold text-emerald-400" dir="ltr">{fmtAmt(t.amount)} {t.type.startsWith('token') ? t.data?.symbol : 'AMAN'}</span>
                </div>
              </div>
            ))}
            {!txsData && <Spinner />}
          </div>
        </div>
      </div>

      {/* Security features */}
      <div>
        <SectionHeader icon={ShieldCheck} title="Six layers of defense" desc="Built into the core of consensus — no configuration, no add-ons" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {features.map(f => (
            <div key={f.title} className="glass glass-hover p-5">
              <div className="rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/25 text-emerald-400 p-2.5 w-fit mb-3">
                <f.icon className="size-5" />
              </div>
              <div className="font-bold text-sm">{f.title}</div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1.5">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
